import { useState, useEffect, useRef, useCallback } from 'react'
import emailjs from '@emailjs/browser'
import './ChatWidget.css'

const API_URL     = process.env.REACT_APP_API_URL || 'https://backend.groupeouambo.com'
const POLL_MS     = 4000
const IDLE_MS     = 2 * 60 * 1000   // 2-minute idle timeout
const SUPPORT_EMAIL = 'info@groupeouambo.com'

const EJS_SERVICE        = process.env.REACT_APP_EMAILJS_SERVICE_ID
const EJS_OWNER_TEMPLATE = process.env.REACT_APP_EMAILJS_OWNER_TEMPLATE_ID
const EJS_USER_TEMPLATE  = process.env.REACT_APP_EMAILJS_TEMPLATE_ID
const EJS_KEY            = process.env.REACT_APP_EMAILJS_PUBLIC_KEY

function ChatWidget() {
  const [isOpen, setIsOpen]               = useState(false)
  const [message, setMessage]             = useState('')
  const [visitorName, setVisitorName]     = useState(() => localStorage.getItem('chat_visitor_name') || '')
  const [visitorEmail, setVisitorEmail]   = useState(() => localStorage.getItem('chat_visitor_email') || '')
  const [hasName, setHasName]             = useState(
    () => !!(localStorage.getItem('chat_visitor_name') && localStorage.getItem('chat_visitor_email'))
  )
  const [messages, setMessages]           = useState([])
  const [unreadCount, setUnreadCount]     = useState(0)
  const [aiLoading, setAiLoading]         = useState(false)
  const [agentOfferState, setAgentOfferState] = useState(() =>
    localStorage.getItem('chat_agent_accepted') === '1' ? 'accepted' : null
  )
  const [chatEnded, setChatEnded]         = useState(false)
  const [transcriptSent, setTranscriptSent] = useState(false)

  const messagesEndRef = useRef(null)
  const pollRef        = useRef(null)
  const idleRef        = useRef(null)
  const lastCountRef   = useRef(0)
  const messagesRef    = useRef([])   // always-current snapshot for timeout callback

  const [sessionId] = useState(() => {
    const stored = localStorage.getItem('chat_session_id')
    if (stored) return stored
    const id = Date.now().toString(36) + Math.random().toString(36).slice(2)
    localStorage.setItem('chat_session_id', id)
    return id
  })

  // Keep messagesRef in sync
  useEffect(() => { messagesRef.current = messages }, [messages])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, agentOfferState, chatEnded])

  // ── Transcript ────────────────────────────────────────────────────
  const sendTranscript = useCallback((name, email, msgs) => {
    if (!msgs.length || !email) return
    const lines = msgs.map(m => {
      const who  = m.sender === 'admin' ? 'Support Agent' : m.sender === 'ai' ? 'AI Assistant' : name
      const time = new Date(m.timestamp).toLocaleString()
      return `[${time}] ${who}: ${m.text}`
    })
    const subject = `Chat transcript — ${name} (${new Date().toLocaleDateString()})`
    const content = lines.join('\n')

    // Send to owner (info@groupeouambo.com)
    emailjs.send(
      EJS_SERVICE,
      EJS_OWNER_TEMPLATE,
      { subject, email, content, to_email: SUPPORT_EMAIL },
      EJS_KEY
    ).catch(console.error)

    // Send copy to the visitor
    emailjs.send(
      EJS_SERVICE,
      EJS_USER_TEMPLATE,
      { subject, email, content },
      EJS_KEY
    ).catch(console.error)
  }, [])

  // ── Idle timer ────────────────────────────────────────────────────
  const stopChat = useCallback((name, email) => {
    clearTimeout(idleRef.current)
    clearInterval(pollRef.current)
    sendTranscript(name || visitorName, email || visitorEmail, messagesRef.current)
    // Clear session so a page refresh starts a fresh chat
    localStorage.removeItem('chat_session_id')
    localStorage.removeItem('chat_visitor_name')
    localStorage.removeItem('chat_visitor_email')
    localStorage.removeItem('chat_agent_accepted')
    setChatEnded(true)
    setTranscriptSent(true)
  }, [sendTranscript, visitorName, visitorEmail])

  const resetIdle = useCallback(() => {
    clearTimeout(idleRef.current)
    idleRef.current = setTimeout(() => stopChat(), IDLE_MS)
  }, [stopChat])

  // ── Fetch messages ────────────────────────────────────────────────
  const fetchMessages = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/visitors/chat/${sessionId}`)
      if (!res.ok) return
      const rows = await res.json()
      const msgs = rows.map(r => ({
        id: r.id, sender: r.sender, name: r.name, text: r.text, timestamp: r.sent_at,
      }))
      setMessages(msgs)
      if (!isOpen && msgs.length > lastCountRef.current) {
        const newMsgs = msgs.slice(lastCountRef.current).filter(m => m.sender !== 'visitor')
        if (newMsgs.length > 0) setUnreadCount(c => c + newMsgs.length)
      }
      lastCountRef.current = msgs.length
    } catch { /* silently ignore */ }
  }, [sessionId, isOpen])

  // Start polling when chat is open and user is known
  useEffect(() => {
    if (!hasName || chatEnded) return
    fetchMessages()
    pollRef.current = setInterval(fetchMessages, POLL_MS)
    return () => clearInterval(pollRef.current)
  }, [fetchMessages, hasName, chatEnded])

  const handleToggle = () => {
    setIsOpen(prev => {
      if (!prev) setUnreadCount(0)
      return !prev
    })
  }

  const postMsg = async (sender, text, name) => {
    const res = await fetch(`${API_URL}/visitors/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, sender, name: name || visitorName, text }),
    })
    if (!res.ok) throw new Error('send failed')
    return res.json()
  }

  const sendAiReply = async (userText, name) => {
    setAiLoading(true)
    try {
      const res = await fetch(`${API_URL}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userText, visitorName: name || visitorName }),
      })
      const data = await res.json()
      const reply = data.reply || "Thank you for your message! How can I help you today?"
      await postMsg('ai', reply)
      if (agentOfferState !== 'accepted') setAgentOfferState('showing')
    } catch {
      await postMsg('ai', "I'm here to help! Ask me anything about Groupe Ouambo's services.").catch(() => {})
      if (agentOfferState !== 'accepted') setAgentOfferState('showing')
    } finally {
      setAiLoading(false)
      fetchMessages()
    }
  }

  const handleNameSubmit = async (e) => {
    e.preventDefault()
    const name  = visitorName.trim()
    const email = visitorEmail.trim()
    if (!name || !email) return
    localStorage.setItem('chat_visitor_name', name)
    localStorage.setItem('chat_visitor_email', email)
    setHasName(true)
    resetIdle()
    const welcomeText = `Hello! I'm ${name}. I'd like to learn more about your services.`
    try {
      await postMsg('visitor', welcomeText, name)
      fetchMessages()
      await sendAiReply(welcomeText, name)
    } catch (err) { console.error('Welcome error:', err) }
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!message.trim() || aiLoading || chatEnded) return
    const text = message.trim()
    setMessage('')
    resetIdle()
    try {
      await postMsg('visitor', text)
      fetchMessages()
      await sendAiReply(text)
    } catch (err) {
      console.error('Send error:', err)
      setMessage(text)
    }
  }

  const handleAgentYes = async () => {
    setAgentOfferState('accepted')
    localStorage.setItem('chat_agent_accepted', '1')
    resetIdle()
    try {
      await postMsg('ai', `An agent has been notified and will be with you shortly, ${visitorName}. Feel free to keep asking me questions!`)
      fetchMessages()
    } catch (err) { console.error('Agent error:', err) }
  }

  const handleAgentNo = () => { setAgentOfferState(null); resetIdle() }

  const handleEndChat = () => stopChat()

  const getSenderLabel = (msg) => {
    if (msg.sender === 'admin') return 'Support Agent'
    if (msg.sender === 'ai') return 'AI Assistant'
    return msg.name || visitorName || 'You'
  }

  const getMsgClass = (sender) => {
    if (sender === 'visitor') return 'visitor'
    if (sender === 'admin') return 'admin'
    return 'ai'
  }

  return (
    <>
      <button className="chat-widget-button" onClick={handleToggle} title="Chat with us">
        💬
        {!isOpen && unreadCount > 0 && (
          <span className="chat-widget-badge">{unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="chat-widget-window">
          <div className="chat-widget-header">
            <div>
              <h4>Chat with us</h4>
              <p>
                {chatEnded
                  ? 'Chat session ended'
                  : agentOfferState === 'accepted'
                    ? 'Agent on the way · AI also here to help'
                    : 'AI-powered support · Human agents available'}
              </p>
            </div>
            <button className="chat-widget-close" onClick={handleToggle}>✕</button>
          </div>

          {/* ── Step 1: collect name + email ── */}
          {!hasName ? (
            <div className="chat-widget-intro">
              <div className="chat-widget-intro-icon">👋</div>
              <h3>Welcome!</h3>
              <p>Please tell us who you are to get started</p>
              <form onSubmit={handleNameSubmit}>
                <input
                  type="text"
                  value={visitorName}
                  onChange={e => setVisitorName(e.target.value)}
                  placeholder="Your name"
                  className="chat-widget-input"
                  required
                />
                <input
                  type="email"
                  value={visitorEmail}
                  onChange={e => setVisitorEmail(e.target.value)}
                  placeholder="Your email address"
                  className="chat-widget-input"
                  style={{ marginTop: 8 }}
                  required
                />
                <button type="submit" className="chat-widget-btn">Start Chat</button>
              </form>
            </div>

          ) : chatEnded ? (
            /* ── Chat ended (idle timeout or manual) ── */
            <div className="chat-widget-intro">
              <div className="chat-widget-intro-icon">✅</div>
              <h3>Chat ended</h3>
              <p>
                Thank you, {visitorName}!{' '}
                {transcriptSent
                  ? `A transcript has been sent to ${SUPPORT_EMAIL}.`
                  : 'Your session has been saved.'}
              </p>
              <button
                className="chat-widget-btn"
                onClick={() => window.location.reload()}
              >
                Start New Chat
              </button>
            </div>

          ) : (
            /* ── Active chat ── */
            <>
              <div className="chat-widget-messages">
                {messages.length === 0 ? (
                  <div className="chat-widget-empty">
                    <p>Hello {visitorName}! Ask us anything.</p>
                  </div>
                ) : (
                  <>
                    {messages.map((msg) => (
                      <div key={msg.id} className={`chat-widget-message ${getMsgClass(msg.sender)}`}>
                        <div className="chat-widget-message-content">
                          <div className="chat-widget-message-name">{getSenderLabel(msg)}</div>
                          <div className="chat-widget-message-text">{msg.text}</div>
                          <div className="chat-widget-message-time">
                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    ))}
                    {aiLoading && (
                      <div className="chat-widget-message ai">
                        <div className="chat-widget-message-content">
                          <div className="chat-widget-message-name">AI Assistant</div>
                          <div className="chat-widget-typing"><span /><span /><span /></div>
                        </div>
                      </div>
                    )}
                    {agentOfferState === 'showing' && !aiLoading && (
                      <div className="chat-widget-agent-offer">
                        <p>Would you like to speak with a human agent?</p>
                        <div className="chat-widget-agent-btns">
                          <button className="chat-widget-agent-yes" onClick={handleAgentYes}>Yes, please</button>
                          <button className="chat-widget-agent-no" onClick={handleAgentNo}>No, thanks</button>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              <form className="chat-widget-form" onSubmit={handleSendMessage}>
                <input
                  type="text"
                  value={message}
                  onChange={e => { setMessage(e.target.value); resetIdle() }}
                  placeholder={aiLoading ? 'AI is typing...' : `Message (${visitorName})...`}
                  className="chat-widget-input"
                  disabled={aiLoading}
                />
                <button type="submit" className="chat-widget-send" disabled={aiLoading}>Send</button>
              </form>
              <div style={{ textAlign: 'center', paddingBottom: 6 }}>
                <button
                  onClick={handleEndChat}
                  style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  End chat & receive transcript
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  )
}

export default ChatWidget
